import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import * as React from 'react';

import { createSafeJotaiLocalStorage } from '@heidi/utils/jotai-storage';
import { PANEL_STORAGE_KEY } from '../components/panel/panel.types';
import { SIDEBAR_STORAGE_KEY } from '../components/sidebar/sidebar.types';

/**
 * Jotai atom for storing the panel pinned state (boolean).
 * When true, the current page's panel is shown in pinned state.
 *
 * This state persists to localStorage and survives page refresh.
 */
const pinnedAtom = atomWithStorage<boolean>(
  PANEL_STORAGE_KEY,
  false,
  createSafeJotaiLocalStorage<boolean>()
);

/**
 * Hook for managing global panel pinned state.
 * Uses Jotai atomWithStorage for localStorage persistence across page refresh.
 *
 * @returns Object with isPinned boolean and setIsPinned function
 *
 * @example
 * ```tsx
 * const { isPinned, setIsPinned } = usePinnedState();
 *
 * // Pin the current page's panel
 * setIsPinned(true);
 *
 * // Unpin
 * setIsPinned(false);
 * ```
 */
export function usePinnedState() {
  const [isPinned, setIsPinned] = useAtom(pinnedAtom);

  return { isPinned, setIsPinned };
}

// Export atom for testing purposes
export { pinnedAtom };

// ============================================================================
// Sidebar State
// ============================================================================

/**
 * Jotai atom for storing sidebar expanded/collapsed state.
 *
 * This state persists to localStorage and survives page refresh.
 * Default: true (expanded)
 *
 * Note: `getOnInit: true` ensures localStorage is read synchronously during
 * atom initialization, preventing the flash/flicker from default→stored value
 * that would otherwise occur during React hydration.
 */
const sidebarExpandedAtom = atomWithStorage<boolean>(
  SIDEBAR_STORAGE_KEY,
  true,
  createSafeJotaiLocalStorage<boolean>(),
  {
    getOnInit: true,
  }
);

/**
 * Hook for managing sidebar expanded/collapsed state.
 * Uses Jotai atomWithStorage for localStorage persistence across page refresh.
 *
 * @returns Object with isExpanded state and setIsExpanded function
 *
 * @example
 * ```tsx
 * const { isExpanded, setIsExpanded, toggle } = useSidebarState();
 *
 * // Toggle sidebar
 * toggle();
 *
 * // Explicitly collapse
 * setIsExpanded(false);
 * ```
 */
export function useSidebarState() {
  const [isExpanded, setIsExpanded] = useAtom(sidebarExpandedAtom);

  // Memoize toggle to prevent unnecessary re-renders
  const toggle = React.useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, [setIsExpanded]);

  return { isExpanded, setIsExpanded, toggle };
}

// Export atom for testing purposes
export { sidebarExpandedAtom };
